import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Typography,
    TextField,
    Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

export default function FilePicker({ onFilesChange }) {
    const [attachments, setAttachments] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingName, setEditingName] = useState("");
    const { t } = useTranslation();

    useEffect(() => {
        if (onFilesChange) onFilesChange(attachments);
    }, [attachments, onFilesChange]);

    const handleFilesSelected = (event) => {
        const files = Array.from(event.target.files);
        const newFiles = files.filter(
            (f) => !attachments.some((att) => att.name === f.name && att.size === f.size)
        );
        setAttachments((prev) => [...prev, ...newFiles]);
        event.target.value = null;
    };

    const handleRemove = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
        if (editingIndex === index) {
            setEditingIndex(null);
            setEditingName("");
        }
    };

    const handleStartEditing = (index) => {
        setEditingIndex(index);
        const fileName = attachments[index].name;
        const dotIndex = fileName.lastIndexOf(".");
        setEditingName(dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName);
    };

    const handleNameChange = (e) => {
        setEditingName(e.target.value);
    };

    const handleNameSubmit = (index) => {
        if (!editingName.trim()) return; // prevent empty names
        const file = attachments[index];
        const ext = file.name.includes(".") ? file.name.substring(file.name.lastIndexOf(".")) : "";
        const newName = editingName.trim() + ext;

        try {
            const renamedFile = new File([file], newName, { type: file.type });
            setAttachments((prev) =>
                prev.map((f, i) => (i === index ? renamedFile : f))
            );
        } catch {
            setAttachments((prev) =>
                prev.map((f, i) => (i === index ? { ...f, name: newName } : f))
            );
        }
        setEditingIndex(null);
        setEditingName("");
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditingName("");
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleNameSubmit(index);
        }
        if (e.key === "Escape") {
            e.preventDefault();
            handleCancelEdit();
        }
    };

    return (
        <Box>
            <Button variant="contained" component="label" sx={{ mb: 2 }}>
                {t("file_picker.select_files")}
                <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFilesSelected}
                    accept="image/*,application/pdf"
                />
            </Button>

            {attachments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("file_picker.no_files_selected")}
                </Typography>
            ) : (
                <List>
                    {attachments.map((file, index) => {
                        const isImage = file.type.startsWith("image/");
                        const previewUrl = isImage ? URL.createObjectURL(file) : null;

                        return (
                            <ListItem
                                key={index}
                                sx={{ alignItems: "center" }}
                            >
                                {isImage && (
                                    <Box
                                        component="img"
                                        src={previewUrl}
                                        alt={file.name}
                                        sx={{ width: 50, height: 50, objectFit: "cover", mr: 2, borderRadius: 1 }}
                                        onLoad={() => URL.revokeObjectURL(previewUrl)}
                                    />
                                )}

                                {editingIndex === index ? (
                                    <TextField
                                        value={editingName}
                                        onChange={handleNameChange}
                                        onBlur={() => handleNameSubmit(index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        size="small"
                                        autoFocus
                                        sx={{ flexGrow: 1 }}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                ) : (
                                    <ListItemText
                                        primary={file.name}
                                        secondary={`${(file.size / 1024).toFixed(2)} KB`}
                                        sx={{ cursor: "default", userSelect: "none", flexGrow: 1 }}
                                    />
                                )}

                                <Stack direction="row" spacing={1}>
                                    {editingIndex === index ? (
                                        <>
                                            <IconButton
                                                edge="end"
                                                aria-label={t("file_picker.confirm_rename")}
                                                onClick={() => handleNameSubmit(index)}
                                                size="small"
                                            >
                                                <CheckIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label={t("file_picker.cancel_rename")}
                                                onClick={handleCancelEdit}
                                                size="small"
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <IconButton
                                                edge="end"
                                                aria-label={t("file_picker.rename_file")}
                                                onClick={() => handleStartEditing(index)}
                                                size="small"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label={t("file_picker.delete_file")}
                                                onClick={() => handleRemove(index)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </Stack>
                            </ListItem>
                        );
                    })}
                </List>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t("file_picker.click_to_rename")}
            </Typography>
        </Box>
    );
}
